/**
 * AXIS — shared autonomy data model (pure, no I/O). The trust-rule ledger (X1/X2/X5/X14), the
 * signal-side routing rules (X26), the autonomy action log + composite-scorecard math (X4/X19), and
 * the downstream-target config (X27). Persistence ports live in `server-api/src/actions/`; this module
 * is types + deterministic helpers only.
 *
 * See `docs/plans/action-system.md` §3 (data model) + the decision log `IMPL-AX-*`.
 */
import type { Disposition, DispositionPolicy } from '../engines/triage.js';
import type { SignalPriority } from '../signals/types.js';

// --- Trust rules (§3.1) ---------------------------------------------------------------------------

/** X2 — sender/domain scoped (precise) or category-wide (broad; strictly higher graduation bar). */
export type RuleGrain = 'sender' | 'domain' | 'category';

/** Where a rule came from (X14 provenance — every ledger row says why it exists). */
export type TrustRuleSource = 'quick-tune' | 'graduation' | 'cold-start' | 'manual';

/** X27 — per-rule downstream-target override; falls back to `user.preferences.actionDefaults`. */
export interface TrustRuleTarget {
  todoistProjectId?: string;
  /** Todoist priority 1 (P4/lowest) … 4 (P1/highest) — Todoist API numbering. */
  todoistPriority?: 1 | 2 | 3 | 4;
  taskLabel?: string;
  calendarId?: string;
}

/** One strike (X5): a correction of an auto-rule inside the rolling window. */
export interface TrustStrike {
  at: number;
  reason: string;
}

/**
 * The per-(subject, disposition) trust rule — policy + graduation + strike state in one row (§3.1).
 * Supersedes `dispositionPolicy` as the source of truth; the legacy store is read as a fallback so
 * existing quick-tune rules carry forward (IMPL-AX-05).
 */
export interface TrustRule {
  /** `${userId}__${ruleKey}` — the store doc id. */
  ruleId: string;
  userId: string;
  /** `sender:jane@acme.com#archive` | `domain:calendly.com#archive` | `category:newsletter#archive`. */
  ruleKey: string;
  grain: RuleGrain;
  /** On sender/domain rules: the category this sender counts toward for X3's K-of-senders derivation. */
  category?: string;
  disposition: Disposition;
  policy: DispositionPolicy;
  // --- graduation state (X3) ---
  /** Consecutive approve-as-is outcomes (an edit is action-positive — X6); reset on reject/override. */
  cleanStreak: number;
  /** YYYY-MM-DD the rule first fired — the ≥M-days age floor's anchor. */
  firstSeenDay: string;
  /** Count of DISTINCT days the rule fired (age gate). */
  distinctDays: number;
  /** The last day counted into `distinctDays` (so one busy morning bumps it once). */
  lastSeenDay: string;
  // --- strike state (X5) ---
  strikes: TrustStrike[];
  // --- proposal state (X18) ---
  /** Set when the owner declined a graduation proposal — cool-off before re-proposing. */
  graduationDeclinedAt?: number;
  /** Set while a graduation-proposal card is live for this rule (don't re-propose). */
  proposalPendingSince?: number;
  // --- provenance / sync (X14/X8) ---
  source: TrustRuleSource;
  /**
   * The linked accounts this rule's mail has been OBSERVED on — stamped from each outcome's source
   * account. Filter sync (X8) mirrors the rule into exactly these mailboxes (owner decision,
   * 2026-07-23 interview: "the rule should apply to whatever account the email is linked to").
   */
  accountIds?: string[];
  /** Lapo-owned Gmail filter per mailbox (`accountId → filterId`) mirroring this rule (X8). */
  gmailFilterIds?: Record<string, string>;
  /** @deprecated superseded by {@link gmailFilterIds}; read for rows written before 2026-07-23. */
  gmailFilterId?: string;
  /** X27 — optional per-rule downstream-target override. */
  target?: TrustRuleTarget;
  createdAt: number;
  updatedAt: number;
}

/** Build the canonical rule key (IMPL-AX-05). */
export function trustRuleKey(grain: RuleGrain, subject: string, disposition: Disposition): string {
  return `${grain}:${subject.toLowerCase()}#${disposition}`;
}

/** Parse a rule key back into its parts; null for a malformed key. */
export function parseTrustRuleKey(
  ruleKey: string,
): { grain: RuleGrain; subject: string; disposition: Disposition } | null {
  const m = /^(sender|domain|category):(.+)#([a-z-]+)$/.exec(ruleKey);
  if (!m) return null;
  return { grain: m[1] as RuleGrain, subject: m[2]!, disposition: m[3] as Disposition };
}

// --- Routing rules (§3.7, X26) --------------------------------------------------------------------

/** X26 — card · audit-line · never-show. */
export type Loudness = 'loud' | 'quiet' | 'suppress';

export type RoutingRuleSource = 'quick-tune' | 'manual';

/**
 * The signal-side tuning axis (X26): what happens to the SIGNAL, orthogonal to the action/trust rule.
 * A different rule family from {@link TrustRule} — routing is owner preference, never earned, never
 * graduates. Suppression governs the signal only; it NEVER grants autonomy (X31).
 */
export interface RoutingRule {
  ruleId: string; // `${userId}__${ruleKey}`
  userId: string;
  /** `kind:<SignalKind>` | `sender:<addr>` | `kind:<SignalKind>|sender:<addr>` (most-specific wins). */
  ruleKey: string;
  loudness?: Loudness;
  priorityOverride?: SignalPriority;
  /** Collapse this kind into one card (client-side grouping in v1 — IMPL-AX-14). */
  batch?: boolean;
  /** Standing snooze: emit already snoozed this many days ("always snooze until morning"). */
  snoozeDefaultDays?: number;
  /** Per-kind TTL override, epoch-ms duration. */
  ttlOverrideMs?: number;
  source: RoutingRuleSource;
  createdAt: number;
  updatedAt: number;
}

/** Routing rule-key builders + the most-specific-wins resolution order for a (kind, sender) pair. */
export function routingKeyForKind(kind: string): string {
  return `kind:${kind}`;
}
export function routingKeyForSender(sender: string): string {
  return `sender:${sender.toLowerCase()}`;
}
export function routingKeyForKindSender(kind: string, sender: string): string {
  return `kind:${kind}|sender:${sender.toLowerCase()}`;
}
/** Keys to try in order (most-specific first) when resolving the routing rule for a signal. */
export function routingLookupKeys(kind: string, sender?: string): string[] {
  const keys: string[] = [];
  if (sender) keys.push(routingKeyForKindSender(kind, sender), routingKeyForSender(sender));
  keys.push(routingKeyForKind(kind));
  return keys;
}

// --- Autonomy log + scorecard (§3.3/§3.4, X4/X19) -------------------------------------------------

/** How an item was handled. */
export type AutonomyMode = 'auto' | 'confirm' | 'escalated';

/** What happened to it. `suppressed` = dropped by a routing rule (excluded from itemsTotal — §3.4). */
export type AutonomyOutcome =
  | 'executed' // auto-executed without a tap (X4)
  | 'approved'
  | 'edited'
  | 'rejected'
  | 'undone' // an executed auto-action reversed by the owner (correction — X5)
  | 'demoted' // a rule lost autonomy on its 2nd strike (X5) — informational recap row, not an item
  | 'suppressed';

/** One autonomy-relevant event (§3.3). The recap, scorecard, and bulk-undo all read this log. */
export interface AutonomyEvent {
  eventId: string;
  userId: string;
  /** YYYY-MM-DD (user-local day the event happened). */
  dateKey: string;
  ruleKey: string;
  disposition: Disposition;
  mode: AutonomyMode;
  outcome: AutonomyOutcome;
  reversible: boolean;
  /** Opaque inverse-op receipt the registry `undo` replays (IMPL-AX-11). Absent ⇒ not undoable. */
  undoToken?: string;
  /** Human line for the recap ("Archived: ACME newsletter"). */
  title?: string;
  signalId?: string;
  /**
   * TUNE U8 — the owner's explicit quality vote on this auto action (recap Good/Not good). Stamped
   * once; `not-good` records the graduation strike, so a LATER undo must not strike again.
   */
  qualityFeedback?: 'good' | 'not-good';
  at: number;
}

/** The composite scorecard for one day (X19 / §3.4). */
export interface AutonomyScorecardDay {
  dateKey: string;
  itemsTotal: number;
  itemsRequiringTap: number;
  autoActions: number;
  autoCorrected: number;
  clearedCount: number;
  suppressedCount: number;
  /** `itemsRequiringTap / itemsTotal` (0 when no items). Trend ↓. */
  interventionRate: number;
  /** `1 − autoCorrected / autoActions` (1 when no auto actions). Trend ↑ — the guardrail. */
  correctAutonomyRate: number;
}

/**
 * Pure scorecard math (X19, assertable — §8.1(4)). `suppressed` events are EXCLUDED from
 * `itemsTotal` (anti-gaming, §3.4) and surfaced as `suppressedCount` instead.
 */
export function computeScorecardDay(dateKey: string, events: AutonomyEvent[]): AutonomyScorecardDay {
  const day = events.filter((e) => e.dateKey === dateKey);
  const suppressed = day.filter((e) => e.outcome === 'suppressed');
  // One log event = one item. An undo flips the event's outcome executed→undone IN PLACE (the log never
  // grows a shadow row), so an `undone` event is still an auto action — just a corrected one. `demoted`
  // is an informational recap marker, not an item.
  const items = day.filter((e) => e.outcome !== 'suppressed' && e.outcome !== 'demoted');
  const itemsTotal = items.length;
  const itemsRequiringTap = items.filter((e) => e.outcome === 'approved' || e.outcome === 'edited' || e.outcome === 'rejected').length;
  const autoActions = items.filter((e) => e.mode === 'auto' && (e.outcome === 'executed' || e.outcome === 'undone')).length;
  const autoCorrected = items.filter((e) => e.outcome === 'undone').length;
  const clearedCount = items.filter((e) => e.outcome === 'executed' || e.outcome === 'approved' || e.outcome === 'edited').length;
  return {
    dateKey,
    itemsTotal,
    itemsRequiringTap,
    autoActions,
    autoCorrected,
    clearedCount,
    suppressedCount: suppressed.length,
    interventionRate: itemsTotal === 0 ? 0 : itemsRequiringTap / itemsTotal,
    correctAutonomyRate: autoActions === 0 ? 1 : Math.max(0, 1 - autoCorrected / autoActions),
  };
}

/** Scorecard across a set of days, sorted ascending by dateKey — the panel's sparkline input. */
export function computeScorecard(events: AutonomyEvent[]): AutonomyScorecardDay[] {
  const days = [...new Set(events.map((e) => e.dateKey))].sort();
  return days.map((d) => computeScorecardDay(d, events));
}
